import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Alert, Flex, Form, Input, Select, Space, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useState } from "react";
import { useAgents } from "@/common/artemis-operations/agents";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { useCreateJob } from "@/common/artemis-operations/jobs";
import { useProject, useProjects } from "@/common/artemis-operations/projects";
import {
	ERROR_NETWORK_DESC,
	ERROR_NETWORK_TITLE,
	JOB_CREATED_DESC,
	JOB_CREATED_TITLE,
} from "@/common/messages";
import { EnvironmentClassification } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { AgentSelector } from "@/components/agents/agent-selector";
import { CronPicker } from "@/components/cron-picker";
import { EnvironmentClassificationBadge } from "@/components/environments/environment-badges";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";
import { SegmentedSelection } from "@/components/segmented-selection";
import { Wizard } from "@/components/wizard";

/** Values used to pre-fill the wizard (e.g. scheduling a job from a task template). */
export interface JobWizardInitialValues {
	name: string;
	description: string;
	projectId?: string | null;
	instructions?: string | null;
	deploymentIds?: string[] | null;
	environmentIds?: string[] | null;
	agentId?: string | null;
}

interface JobCreateWizardProps {
	open: boolean;
	onClose: () => void;
	/** When provided, the wizard opens pre-filled with these values. */
	initialValues?: JobWizardInitialValues;
}

export function JobCreateWizard({
	open,
	onClose,
	initialValues,
}: JobCreateWizardProps) {
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId: string;
	};

	const [form] = Form.useForm();
	// Whether the job targets a project (with optional deployments) or a set of
	// environments directly, with no project at all.
	const [targetMode, setTargetMode] = useState<"project" | "environment">(
		"project",
	);
	const { mutate: createJob, isPending } = useCreateJob();
	const { data: projects } = useProjects(workspaceId);
	const { data: agents } = useAgents(workspaceId);
	const { data: environments } = useEnvironments(workspaceId);
	const queryClient = useQueryClient();
	const notify = useNotify();

	const values = Form.useWatch([], form);
	const { data: project } = useProject(values?.projectId);

	const deploymentOptions = (project?.deployments ?? []).map((d) => ({
		value: d.id,
		label: d.name,
	}));

	const reset = () => {
		setTargetMode("project");
		form.resetFields();
	};

	// Seed the form whenever the wizard opens with initial values (schedule-from-template flow).
	useEffect(() => {
		if (open && initialValues) {
			setTargetMode(initialValues.projectId ? "project" : "environment");
			form.setFieldsValue({
				name: initialValues.name,
				description: initialValues.description,
				projectId: initialValues.projectId ?? undefined,
				deploymentIds: initialValues.deploymentIds ?? [],
				environmentIds: initialValues.environmentIds ?? [],
				agentId: initialValues.agentId ?? undefined,
				instructions: initialValues.instructions ?? undefined,
			});
		}
	}, [open, initialValues, form]);

	const handleComplete = () => {
		form.validateFields().then((vals) => {
			createJob(
				{
					name: vals.name,
					description: vals.description,
					workspaceId,
					projectId: targetMode === "project" ? vals.projectId : undefined,
					agentId: vals.agentId,
					instructions: vals.instructions ?? null,
					deploymentIds:
						targetMode === "project" ? (vals.deploymentIds ?? []) : [],
					environmentIds: vals.environmentIds ?? [],
					schedule: vals.schedule,
				},
				{
					onSuccess: () => {
						notify.success(JOB_CREATED_TITLE, JOB_CREATED_DESC);
						queryClient.invalidateQueries({ queryKey: ["jobs"] });
						reset();
						onClose();
					},
					onError: () => {
						notify.error(ERROR_NETWORK_TITLE, ERROR_NETWORK_DESC);
					},
				},
			);
		});
	};

	// A scheduled job needs something to target (a project or an environment)
	// and an enabled agent to run as.
	const hasProject = (projects ?? []).some((p) => p != null);
	const hasEnvironment = (environments ?? []).some((e) => e != null);
	const hasAgent = (agents ?? []).some((a) => a?.status === "ENABLED");

	if ((!hasProject && !hasEnvironment) || !hasAgent) {
		return (
			<CustomDrawer
				title="Create Scheduled Job"
				open={open}
				onClose={onClose}
				size="large"
			>
				<Alert
					type="info"
					title="Set up a target and an agent first"
					description="To run a background job you need at least one project or environment and one enabled agent in this workspace. Add an agent and a project or environment, then create the job."
				/>
			</CustomDrawer>
		);
	}

	return (
		<Wizard
			open={open}
			title="Create Scheduled Job"
			completeLabel="Create"
			onComplete={handleComplete}
			form={form}
			loading={isPending}
			size="large"
			onCancel={() => {
				onClose();
				reset();
			}}
			pages={[
				{
					id: "details",
					title: "Details",
					content: (
						<Space vertical size="small" style={{ width: "100%" }}>
							<Form.Item
								name="name"
								label="Name"
								rules={[{ required: true, min: 3, max: 500 }]}
							>
								<Input maxLength={500} placeholder="Job name" />
							</Form.Item>
							{/* Required: each run copies it into the run's task description, which is
							    what the agent receives as its message. */}
							<Form.Item
								name="description"
								label="Description"
								rules={[{ required: true, min: 10, max: 5000 }]}
							>
								<Input.TextArea
									rows={3}
									maxLength={5000}
									showCount
									placeholder="Describe the job"
								/>
							</Form.Item>
						</Space>
					),
				},
				{
					id: "project",
					title: "Target",
					content: (
						<Space vertical size="middle" style={{ width: "100%" }}>
							<Form.Item label="Target">
								<SegmentedSelection
									value={targetMode}
									onChange={(v) => {
										setTargetMode(v as "project" | "environment");
										form.setFieldsValue({
											projectId: undefined,
											deploymentIds: [],
											environmentIds: [],
										});
									}}
									options={[
										{
											value: "project",
											label: "Project job",
											description:
												"Each run works within a project and its deployments.",
										},
										{
											value: "environment",
											label: "Environment job",
											description:
												"Each run works directly on one or more environments — no project.",
										},
									]}
								/>
							</Form.Item>
							{targetMode === "project" && (
								<>
									<Form.Item
										name="projectId"
										label="Project"
										rules={[{ required: true }]}
									>
										<Select
											placeholder="Select a project"
											options={(projects ?? [])
												.filter((p) => p != null)
												.map((p) => ({ value: p.id, label: p.name }))}
											onChange={() => form.setFieldValue("deploymentIds", [])}
										/>
									</Form.Item>
									<Form.Item
										name="deploymentIds"
										label="Deployments"
										style={{ marginBottom: 0 }}
										extra="Optional - scope each run to specific deployments. The assigned agent resolves each deployment's environment automatically."
									>
										<Select
											mode="multiple"
											placeholder="Select deployments"
											options={deploymentOptions}
											optionRender={(option) => (
												<Flex align="center" justify="space-between" gap={8}>
													<Typography.Text>{option.data.label}</Typography.Text>
													<EnvironmentClassificationBadge
														classification={
															environments?.find(
																(e) => e?.id === option.data.value,
															)?.classification ??
															EnvironmentClassification.NON_PRODUCTION
														}
													/>
												</Flex>
											)}
											notFoundContent={
												<Typography.Text type="secondary">
													{values?.projectId
														? "This project has no deployments yet."
														: "Select a project first."}
												</Typography.Text>
											}
										/>
									</Form.Item>
								</>
							)}
							{targetMode === "environment" && (
								<Form.Item
									name="environmentIds"
									label="Environments"
									rules={[
										{
											required: true,
											message: "Select at least one environment",
										},
									]}
									extra="The environments each run operates on."
								>
									<Select
										mode="multiple"
										placeholder="Select environments"
										options={(environments ?? [])
											.filter((e) => e != null)
											.map((e) => ({ value: e.id, label: e.name }))}
										optionRender={(option) => (
											<Flex align="center" justify="space-between" gap={8}>
												<Typography.Text>{option.data.label}</Typography.Text>
												<EnvironmentClassificationBadge
													classification={
														environments?.find(
															(e) => e?.id === option.data.value,
														)?.classification ??
														EnvironmentClassification.NON_PRODUCTION
													}
												/>
											</Flex>
										)}
									/>
								</Form.Item>
							)}
						</Space>
					),
				},
				{
					id: "schedule",
					title: "Schedule",
					content: (
						<Space vertical size="small" style={{ width: "100%" }}>
							<Form.Item
								name="schedule"
								label="Cron schedule"
								rules={[
									{ required: true, message: "Select or enter a schedule" },
								]}
							>
								<CronPicker />
							</Form.Item>
						</Space>
					),
				},
				{
					id: "agent",
					title: "Agent",
					content: (
						<Space vertical size="small" style={{ width: "100%" }}>
							<AgentSelector
								agentRequired
								workspaceId={workspaceId}
								fieldName="agentId"
								onAgentChange={(id) => form.setFieldValue("agentId", id)}
							/>
							<Form.Item
								name="instructions"
								label="Agent instructions"
								extra="Instructions passed to the agent on each run. Describe what the agent should do, what data to analyze, and what artifacts to produce."
							>
								<TextArea
									rows={12}
									placeholder="e.g. Analyze the past 24h of error logs in the production environment. Summarize any recurring errors and create a markdown report. Upload the report as an artifact."
								/>
							</Form.Item>
						</Space>
					),
				},
			]}
		/>
	);
}
